import { useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { RulePicker } from './RulePicker.js';

interface SimulationResult {
  readonly status: string;
  readonly impactSummary: Record<string, unknown> | null;
  /** The rule as picked when the simulation was run. */
  readonly ruleLabel: string;
}

export function M24SimulationCenterPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [ruleId, setRuleId] = useState('');
  const [ruleLabel, setRuleLabel] = useState('');
  const [simulationName, setSimulationName] = useState('');
  const [scenarioData, setScenarioData] = useState('{\n  "amount": 500\n}');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SimulationResult | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    setError(null);
    setResult(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(scenarioData);
    } catch {
      setError(t('m24.simulation.invalidJson'));
      return;
    }
    setSubmitting(true);
    apiRequest<{ data: Omit<SimulationResult, 'ruleLabel'> }>('/v1/rule-simulations', {
      method: 'POST',
      token,
      body: { ruleId, simulationName: simulationName || undefined, scenarioData: parsed },
    })
      .then((r) => { setResult({ ...r.data, ruleLabel }); })
      .catch((err) => setError((err as Error).message))
      .finally(() => setSubmitting(false));
  }

  return (
    <main aria-label="simulation-center">
      <h1>{t('m24.simulation.title')}</h1>
      <form aria-label="simulation-form" onSubmit={onSubmit}>
        <RulePicker label={t('m24.simulation.ruleId')} value={ruleId} onPick={(id, label) => { setRuleId(id); setRuleLabel(label); }} />
        <label htmlFor="simulationName">{t('m24.simulation.simulationName')}</label>
        <input id="simulationName" value={simulationName} onChange={(e) => setSimulationName(e.target.value)} />
        <label htmlFor="scenarioData">{t('m24.simulation.scenarioData')}</label>
        <textarea id="scenarioData" value={scenarioData} onChange={(e) => setScenarioData(e.target.value)} rows={6} />
        <button type="submit" disabled={submitting || !ruleId.trim() || !scenarioData.trim()}>{t('m24.simulation.submit')}</button>
      </form>
      {result && (
        <section aria-label="simulation-result" role="status">
          <p>{t('m24.simulation.resultLine', { status: result.status, rule: result.ruleLabel })}</p>
          {result.impactSummary && (
            <table aria-label="impact-summary">
              <thead><tr><th>{t('m24.simulation.colMetric')}</th><th>{t('m24.simulation.colValue')}</th></tr></thead>
              <tbody>
                {Object.entries(result.impactSummary).map(([k, v]) => (
                  <tr key={k}><td>{k}</td><td>{String(v)}</td></tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
      {error && <p role="alert">{error}</p>}
    </main>
  );
}
